import {
  booleanAttribute,
  Component,
  Input,
  numberAttribute,
  OnChanges,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder } from '@angular/forms';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { DocumentVisibilityKind } from '../../../common/model/const/enum';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { BuildingDocument } from '../../../rent/model/building-document';
import { BuildingDocumentType, RentalObjectDocumentType } from '../../../rent/model/const/documentEnums';
import { RentalObjectDocument } from '../../../rent/model/rental-object-document';
import { BuildingDocumentService } from '../../../rent/service/data/building-document.service';
import { RentalObjectDocumentService } from '../../../rent/service/data/rental-object-document.service';
import { IUploadedDocument } from '../../model/uploaded-document';

@Component({
    selector: 'letting-document-control',
    template: `
    <section class="astral-section-surface astral-stack-m">
      <div class="astral-stack-s">
        <h5>{{ control.caption | translate }}</h5>
        <span>{{ (isBuilding ? 'LETTING.ADVERTISMENT.SAVED_ON_BUILDING' : 'LETTING.ADVERTISMENT.SAVED_ON_OBJECT') | translate }}</span>
      </div>
      <app-multi-file-upload-sortable
        [allowedMimeTypes]="mimeTypes"
        [forms]="formArray"
        [maxFiles]="maxFiles"
        [readonly]="control.isReadonly"
        (fileAdded)="added($event)"
        (fileRemoved)="removed($event)"
        (filesReordered)="reordered($event)"
      ></app-multi-file-upload-sortable>
    </section>
  `,
    styles: [
        `
           :host {
             display: contents;
           }
         `,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LettingDocumentControlComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ transform: booleanAttribute }) isBuilding: boolean = false;
  @Input({ transform: numberAttribute }) maxFiles: number;
  @Input({ required: true }) parentId: string;
  // Widened to AbstractControl so callers can pass form.controls.X (typed AbstractControl)
  // under strictTemplates; internally treated as FormControlEx<IValueDocument[]>.
  private _control: FormControlEx<IValueDocument[]>;
  @Input({ required: true }) set control(v: AbstractControl) {
    this._control = v as FormControlEx<IValueDocument[]>;
    if (this.initialized) {
      this.buildFormArray();
    }
  }
  get control(): FormControlEx<IValueDocument[]> {
    return this._control;
  }
  @Input({ required: true }) documents: IUploadedDocument[];
  @Input({ required: true }) type: RentalObjectDocumentType | BuildingDocumentType;
  @Input({ required: true }) mimeTypes: string[];

  protected formArray: UntypedFormArray;

  private destroy = new Subject<void>();
  private documentChanges = new Subject<void>();
  private initialized = false;

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly formFactory: FormControlExFactory,
    private readonly rentalObjectDocumentService: RentalObjectDocumentService,
    private readonly buildingDocumentService: BuildingDocumentService
  ) {}

  ngOnInit(): void {
    this.initialized = true;
    this.buildFormArray();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.initialized && changes['documents']) {
      this.buildFormArray();
    }
  }

  ngOnDestroy(): void {
    this.documentChanges.next();
    this.documentChanges.complete();
    this.destroy.next();
    this.destroy.complete();
  }

  get isReadonly(): boolean {
    return !!this.control?.isReadonly;
  }

  private buildFormArray(): void {
    this.documentChanges.next();
    this.formArray = new UntypedFormArray([]);
    for (let document of this.documents) {
      let documentForm = this.formBuilder.group({
        id: this.formFactory.createHidden(document.id),
        connectionId: this.formFactory.createHidden(document.connectionId),
        data: this.formFactory.createHidden(null),
        src: this.formFactory.createHidden(document.src),
        mimeType: this.formFactory.createHidden(document.mimeType),
        size: this.formFactory.createHidden(document.size),
        title: this.formFactory.createWithOptions('DOCUMENT.CAPTION', document.title, {
          required: true,
          isReadonly: this.isReadonly,
        }),
        originalName: this.formFactory.createHidden(document.originalName),
      });

      documentForm.controls.title.valueChanges.pipe(takeUntil(this.destroy), takeUntil(this.documentChanges), debounceTime(500)).subscribe({
        next: val => {
          if (this.isReadonly) {
            return;
          }

          const change = [...this.control.value];
          const doc = change.find(x => x.documentId === document.id);
          doc.documentTitle = val;

          this.setControlValue(change);
        },
      });

      this.formArray.controls.push(documentForm);
    }
  }

  added(file: File) {
    if (this.isReadonly) {
      return;
    }

    if (this.isBuilding) {
      let document = new BuildingDocument();
      document.connectionType = this.type as BuildingDocumentType;
      document.caption = file.name;
      document.building = this.parentId;
      document.visibility = DocumentVisibilityKind.Public;
      document.document = file;
      return this.buildingDocumentService.add(document, true).subscribe({
        next: doc => this.handleAddedNewFile(file, doc),
      });
    } else {
      let document = new RentalObjectDocument();
      document.connectionType = this.type as RentalObjectDocumentType;
      document.caption = file.name;
      document.rentalObject = this.parentId;
      document.visibility = DocumentVisibilityKind.Public;
      document.document = file;
      this.rentalObjectDocumentService.add(document, true).subscribe({
        next: doc => this.handleAddedNewFile(file, doc),
      });
    }
  }

  handleAddedNewFile(file: File, doc: RentalObjectDocument | BuildingDocument) {
    if (this.isReadonly) {
      return;
    }

    const fileForm = this.formBuilder.group({
      id: this.formFactory.createHidden(doc.documentId),
      data: this.formFactory.createHidden(file),
      src: this.formFactory.createHidden(null),
      mimeType: this.formFactory.createHidden(file.type),
      size: this.formFactory.createHidden(file.size),
      title: this.formFactory.createMandatory('DOCUMENT.CAPTION', doc.caption),
      originalName: this.formFactory.createHidden(file.name),
    });

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = _event => {
      fileForm.controls['src'].setValue(reader.result);
    };

    this.formArray.push(fileForm);

    this.setControlValue([...this.control.value, { documentId: doc.documentId, id: null, documentTitle: file.name }]);
  }

  removed(index: number) {
    if (this.isReadonly) {
      return;
    }

    const change = [...this.control.value];
    change.splice(index, 1);

    this.setControlValue(change);
  }

  reordered(files: { id: string }[]) {
    if (this.isReadonly) {
      return;
    }

    var change = [...this.control.value];
    change.sort((a, b) => files.findIndex(x => a.documentId === x.id) - files.findIndex(x => b.documentId === x.id));
    this.setControlValue(change);
  }

  private setControlValue(documents: IValueDocument[]) {
    if (this.isReadonly) {
      return;
    }

    this.control.setValue(documents);
    this.control.markAsDirty();
  }
}

interface IValueDocument {
  documentId: string;
  id: string;
  documentTitle: string;
}
